import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Target } from 'lucide-react';
import { apiClient } from '../../api/client';
import { QuantitativeDataTable, Lifecycle5State, TableColumn } from '../../components/common/QuantitativeDataTable';
import { formatPrice, formatPercent } from '../../shared/domain/formatters';
import { compareDecimalStrings, getDecimalSign } from '../../shared/domain/decimal';
import { ASHARE_BILINGUAL } from '../../shared/constants/bilingualTitles';

export interface AmbushRadarCandidateItem {
  snapshot_id: string;
  run_id: string;
  trade_date: string;
  session_kind: string;
  thscode: string;
  symbol: string;
  name: string;
  board_type: string;
  sector_name: string;
  ambush_score: string;
  benchmark_price: string;
  latest_pct_chg: string;
  market_rs_diff: string;
  quant_signals: string[];
  quant_details: Record<string, any>;
  computed_at_utc?: string;
}

export interface AmbushRadarPoolResponse {
  status: 'fresh' | 'stale' | 'empty' | 'disconnected' | 'loading' | string;
  trade_date?: string;
  session_kind: string;
  total: number;
  limit: number;
  offset: number;
  items: AmbushRadarCandidateItem[];
  meta?: {
    state: string;
    run_id?: string;
    rule_version?: string;
    sector_registry_version?: string;
    total_universe?: number;
    evaluated_count?: number;
    candidate_count?: number;
    coverage_ratio?: string;
    benchmark_code?: string;
    benchmark_pct_chg?: string;
    scan_completed_at?: string;
    stale_reason?: string;
    message?: string;
  };
}

export interface AShareAmbushRadarViewProps {
  colorMode?: 'mainland' | 'international';
  onToggleColorMode?: () => void;
}

export const AShareAmbushRadarView: React.FC<AShareAmbushRadarViewProps> = ({
  colorMode = 'mainland',
  onToggleColorMode,
}) => {
  const [radarData, setRadarData] = useState<AmbushRadarCandidateItem[]>([]);
  const [radarState, setRadarState] = useState<Lifecycle5State>('loading');
  const [radarErrorMessage, setRadarErrorMessage] = useState<string | null>(null);
  const [radarMeta, setRadarMeta] = useState<AmbushRadarPoolResponse['meta'] | null>(null);
  const [radarSector, setRadarSector] = useState<string>('ALL');
  const [radarMinScore, setRadarMinScore] = useState<'ALL' | '80' | '100' | '115'>('ALL');
  const [radarTradeDate, setRadarTradeDate] = useState<string>('--');

  const fetchRadarData = useCallback(async () => {
    setRadarState('loading');
    setRadarErrorMessage(null);
    try {
      const res = await apiClient.get<AmbushRadarPoolResponse>('/api/v1/market/a-share/radar/pool', {
        params: {
          session_kind: 'DAY_CLOSE',
          limit: 200,
        },
      });
      const st = ['loading', 'fresh', 'stale', 'disconnected', 'empty'].includes(res.status) ? res.status : 'fresh';
      setRadarData(res.items || []);
      setRadarState(st as Lifecycle5State);
      setRadarMeta(res.meta || null);
      setRadarTradeDate(res.trade_date || '--');
    } catch (err: any) {
      setRadarState('disconnected');
      setRadarErrorMessage(err?.message || '无法连接到行情特征打标服务');
      setRadarData([]);
    }
  }, []);

  useEffect(() => {
    fetchRadarData();
  }, [fetchRadarData]);

  const getThsUrl = (symbol: string) => `https://basic.10jqka.com.cn/${symbol}/`;
  const getEastmoneyUrl = (symbol: string, thscode: string) => {
    const market = thscode.endsWith('.SH') ? 'sh' : (thscode.endsWith('.BJ') ? 'bj' : 'sz');
    return `https://quote.eastmoney.com/${market}${symbol}.html`;
  };

  const availableSectors = useMemo(() => {
    const set = new Set<string>();
    radarData.forEach((item) => {
      if (item.sector_name) set.add(item.sector_name);
    });
    return Array.from(set).sort();
  }, [radarData]);

  const filteredRadarData = useMemo(() => {
    return radarData.filter((item) => {
      if (radarSector !== 'ALL' && item.sector_name !== radarSector) {
        return false;
      }
      if (radarMinScore !== 'ALL') {
        const threshold = radarMinScore === '115' ? '115.00' : (radarMinScore === '100' ? '100.00' : '80.00');
        if (compareDecimalStrings(item.ambush_score, threshold) < 0) {
          return false;
        }
      }
      return true;
    });
  }, [radarData, radarSector, radarMinScore]);

  const radarColumns: TableColumn<AmbushRadarCandidateItem>[] = useMemo(() => [
    {
      key: 'symbol',
      title: '代码 / 外链',
      width: '120px',
      render: (r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{r.symbol}</span>
          <div style={{ display: 'flex', gap: '4px', fontSize: '10px' }}>
            <a
              href={getThsUrl(r.symbol)}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--accent-cyan)', textDecoration: 'none' }}
              title="同花顺F10行情"
            >
              同花顺
            </a>
            <span style={{ color: 'var(--text-muted)' }}>·</span>
            <a
              href={getEastmoneyUrl(r.symbol, r.thscode)}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--color-emerald)', textDecoration: 'none' }}
              title="东方财富F10行情"
            >
              东财
            </a>
          </div>
        </div>
      ),
      sortable: true,
      sortValue: (r) => r.symbol,
    },
    {
      key: 'name',
      title: '股票名称',
      width: '110px',
      render: (r) => (
        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
          {r.name}
        </span>
      ),
      sortable: true,
      sortValue: (r) => r.name,
    },
    {
      key: 'sector_name',
      title: '申万行业',
      width: '110px',
      render: (r) => (
        <span style={{
          padding: '2px 6px',
          borderRadius: '3px',
          background: 'var(--badge-info-bg)',
          color: 'var(--accent-cyan)',
          fontSize: '11px',
          fontWeight: 500,
        }}>
          {r.sector_name || '其他综合'}
        </span>
      ),
      sortable: true,
      sortValue: (r) => r.sector_name || '',
    },
    {
      key: 'board_type',
      title: '板块',
      width: '90px',
      render: (r) => (
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
          {r.board_type}
        </span>
      ),
      sortable: true,
      sortValue: (r) => r.board_type,
    },
    {
      key: 'ambush_score',
      title: '潜伏评分',
      width: '100px',
      align: 'right',
      render: (r) => {
        const isSuper = compareDecimalStrings(r.ambush_score, '115.00') >= 0;
        const isHigh = compareDecimalStrings(r.ambush_score, '100.00') >= 0;
        return (
          <span style={{
            padding: '2px 8px',
            borderRadius: '4px',
            background: isSuper ? 'var(--badge-warning-bg)' : (isHigh ? 'var(--badge-bullish-bg)' : 'var(--badge-info-bg)'),
            color: isSuper ? 'var(--color-warning)' : (isHigh ? 'var(--color-emerald)' : 'var(--accent-cyan)'),
            fontWeight: 700,
            fontSize: '12px',
            fontFamily: 'var(--font-mono)',
          }}>
            {r.ambush_score}
          </span>
        );
      },
      sortable: true,
      sortValue: (r) => r.ambush_score,
    },
    {
      key: 'benchmark_price',
      title: '收盘基准价',
      width: '100px',
      align: 'right',
      render: (r) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-primary)' }}>
          {formatPrice(r.benchmark_price)}
        </span>
      ),
      sortable: true,
      sortValue: (r) => r.benchmark_price,
    },
    {
      key: 'latest_pct_chg',
      title: '收盘涨跌幅',
      width: '100px',
      align: 'right',
      render: (r) => {
        const sign = getDecimalSign(r.latest_pct_chg);
        const color = sign > 0
          ? (colorMode === 'mainland' ? 'var(--color-bullish)' : 'var(--color-bearish)')
          : sign < 0
          ? (colorMode === 'mainland' ? 'var(--color-bearish)' : 'var(--color-bullish)')
          : 'var(--text-muted)';
        return (
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color }}>
            {formatPercent(r.latest_pct_chg)}
          </span>
        );
      },
      sortable: true,
      sortValue: (r) => r.latest_pct_chg,
    },
    {
      key: 'market_rs_diff',
      title: '相对大盘RS',
      width: '110px',
      align: 'right',
      render: (r) => {
        const sign = getDecimalSign(r.market_rs_diff);
        const isPositive = sign > 0;
        return (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            color: isPositive ? 'var(--color-emerald)' : 'var(--color-rose)',
          }}>
            {formatPercent(r.market_rs_diff)}
          </span>
        );
      },
      sortable: true,
      sortValue: (r) => r.market_rs_diff,
    },
    {
      key: 'support_levels',
      title: '关键支撑 (S1 / Fib0.618)',
      width: '140px',
      render: (r) => {
        const s1 = r.quant_details?.s1_support;
        const fib = r.quant_details?.fib_0618;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '4px' }}>
              <span style={{ color: 'var(--text-secondary)' }}>S1:</span>
              <span style={{ fontWeight: 600, color: 'var(--accent-cyan)' }}>{s1 ? formatPrice(s1) : '--'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '4px' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Fib:</span>
              <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{fib ? formatPrice(fib) : '--'}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'pivot_entry',
      title: '参考建仓位',
      width: '110px',
      align: 'right',
      render: (r) => {
        const entry = r.quant_details?.pivot_entry_level;
        return (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontWeight: 700,
            fontSize: '12px',
            padding: '2px 6px',
            borderRadius: '4px',
            background: 'var(--badge-bullish-bg)',
            color: 'var(--color-emerald)',
          }}>
            {entry ? formatPrice(entry) : '--'}
          </span>
        );
      },
      sortable: true,
      sortValue: (r) => r.quant_details?.pivot_entry_level || '0',
    },
    {
      key: 'quant_signals',
      title: '量化特征信号云 (≥2项入选)',
      render: (r) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {(r.quant_signals || []).map((sig, idx) => (
            <span
              key={idx}
              style={{
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '3px',
                background: sig.includes('地量') ? 'var(--badge-info-bg)' : (sig.includes('试盘') ? 'var(--badge-warning-bg)' : 'var(--badge-neutral-bg)'),
                color: sig.includes('地量') ? 'var(--accent-cyan)' : (sig.includes('试盘') ? 'var(--color-warning)' : 'var(--text-secondary)'),
                fontWeight: 500,
              }}
            >
              {sig}
            </span>
          ))}
          {r.quant_details?.sector_bonus === '5.00' && (
            <span style={{
              fontSize: '10px',
              padding: '2px 5px',
              borderRadius: '3px',
              background: 'var(--badge-bullish-bg)',
              color: 'var(--color-emerald)',
              fontWeight: 600,
            }}>
              +5 行业加分
            </span>
          )}
        </div>
      ),
    },
  ], [colorMode]);

  return (
    <div className="view-container animate-fade-in" style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* 1. Standardized TSO 2.0 View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <Target size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">{ASHARE_BILINGUAL.ambushRadar.zh}</h2>

            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: radarState === 'fresh' ? 'var(--badge-bullish-bg)' : (radarState === 'stale' ? 'var(--badge-warning-bg)' : 'var(--badge-neutral-bg)'),
                color: radarState === 'fresh' ? 'var(--color-emerald)' : (radarState === 'stale' ? 'var(--color-warning)' : 'var(--text-secondary)'),
                fontWeight: 600,
                fontSize: '11px',
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: radarState === 'fresh' ? 'var(--color-emerald)' : (radarState === 'stale' ? 'var(--color-warning)' : 'var(--text-secondary)'),
                }}
              />
              ● {radarState.toUpperCase()}
            </span>

            <span
              style={{
                fontSize: '11px',
                padding: '1px 6px',
                borderRadius: '3px',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              交易日: <strong style={{ color: 'var(--accent-cyan)' }}>{radarTradeDate}</strong>
            </span>

            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              基准: <strong style={{ color: 'var(--text-primary)' }}>沪深300 ({radarMeta?.benchmark_code || '000300.SH'})</strong>
              <span style={{
                marginLeft: '4px',
                color: (radarMeta?.benchmark_pct_chg && radarMeta.benchmark_pct_chg.startsWith('-')) ? 'var(--color-bearish)' : 'var(--color-bullish)',
                fontWeight: 600,
              }}>
                {radarMeta?.benchmark_pct_chg ? `${radarMeta.benchmark_pct_chg}%` : '--'}
              </span>
            </span>
          </div>
          <span className="bilingual-header-subtitle-en">{ASHARE_BILINGUAL.ambushRadar.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            396 只候选股 · Fibonacci 挂单价计算器 · 全量扫描 {radarMeta?.total_universe || 5563} 只 · 候选入选 {radarData.length} 只
          </p>
        </div>

        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
          <span style={{ color: 'var(--text-secondary)' }}>
            行业标准: <span style={{ color: 'var(--accent-cyan)' }}>{radarMeta?.sector_registry_version || 'v2026.09.1'}</span>
          </span>
        </div>
      </div>

      {/* Ambush Filters Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '6px 12px',
          background: 'var(--bg-canvas)',
          borderRadius: '4px',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)' }}>
            最低评分:
          </span>
          {[
            { key: 'ALL', label: '全部 (≥80)' },
            { key: '100', label: '优质 (≥100)' },
            { key: '115', label: '高分 (≥115)' },
          ].map((tier) => (
            <button
              key={tier.key}
              onClick={() => setRadarMinScore(tier.key as any)}
              style={{
                padding: '2px 8px',
                borderRadius: '3px',
                fontSize: '11px',
                fontWeight: radarMinScore === tier.key ? 600 : 400,
                border: 'none',
                background: radarMinScore === tier.key ? 'var(--btn-primary-bg)' : 'transparent',
                color: radarMinScore === tier.key ? 'var(--btn-primary-text)' : 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              {tier.label}
            </button>
          ))}

          <span style={{ color: 'var(--border-subtle)' }}>|</span>

          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)' }}>
            申万行业:
          </span>
          <select
            value={radarSector}
            onChange={(e) => setRadarSector(e.target.value)}
            style={{
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '11px',
              background: 'var(--bg-card)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-card)',
            }}
          >
            <option value="ALL">全部行业 ({radarData.length})</option>
            {availableSectors.map((sec) => (
              <option key={sec} value={sec}>{sec}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onToggleColorMode && (
            <button
              onClick={onToggleColorMode}
              style={{
                padding: '3px 8px',
                borderRadius: '3px',
                fontSize: '11px',
                background: 'var(--bg-card)',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-card)',
                cursor: 'pointer',
              }}
            >
              {colorMode === 'mainland' ? '🇨🇳 红涨绿跌' : '🌐 绿涨红跌'}
            </button>
          )}
          <button
            onClick={fetchRadarData}
            style={{
              padding: '3px 10px',
              borderRadius: '3px',
              fontSize: '11px',
              background: 'var(--btn-primary-bg)',
              color: 'var(--btn-primary-text)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            刷新雷达
          </button>
        </div>
      </div>

      <QuantitativeDataTable<AmbushRadarCandidateItem>
        data={filteredRadarData}
        columns={radarColumns}
        state={radarState}
        errorMessage={radarErrorMessage}
        onRetry={fetchRadarData}
        pageSize={30}
        pageSizeOptions={[30, 50, 100, 200]}
        rowKey={(r) => r.thscode}
        initialSortKey="ambush_score"
        initialSortDirection="desc"
        searchFilter={(r, q) =>
          (r.name || '').toLowerCase().includes(q) ||
          r.thscode.toLowerCase().includes(q) ||
          (r.sector_name || '').toLowerCase().includes(q)
        }
        searchPlaceholder="在潜伏候选池中搜索 (名称/代码/行业)..."
        emptyMessage="当前潜伏雷达规则 (RADAR_RULE_V1) 下无满足条件的候选标的 (门槛: ≥2量化信号 + 评分≥80)"
      />
    </div>
  );
};
export default AShareAmbushRadarView;
